import { useEffect, useRef, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, API_URL, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, Trash2, Download, ExternalLink, Upload, FileText } from "lucide-react";

const MAX_MB = 10;

export default function SafetySheets() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [brand, setBrand] = useState("");
  const [label, setLabel] = useState("");
  const fileRef = useRef();
  const [dragOver, setDragOver] = useState(false);

  const load = async (search = "") => {
    try {
      const { data } = await api.get("/safety-sheets", { params: search ? { q: search } : {} });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Fichier trop volumineux (max ${MAX_MB} Mo)`);
      return;
    }
    setUploading(true);
    try {
      const data_base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      await api.post("/safety-sheets", {
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        data_base64,
        brand: brand.trim(),
        label: label.trim(),
      });
      toast.success(`${file.name} ajoutée`);
      setBrand(""); setLabel("");
      if (fileRef.current) fileRef.current.value = "";
      load(q);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (files) => {
    Array.from(files).forEach((f) => upload(f));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const del = async (id, filename) => {
    if (!window.confirm(`Supprimer "${filename}" ?`)) return;
    try {
      await api.delete(`/safety-sheets/${id}`);
      toast.success("Supprimée");
      setItems((it) => it.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openInline = (id) => {
    const token = localStorage.getItem("vape_token");
    // Open in new tab with auth via query? Simpler: use fetch then blob URL
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/safety-sheets/${id}/download`, {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok) throw new Error("Impossible d'ouvrir");
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      } catch (e) {
        toast.error(e.message);
      }
    })();
  };

  const download = (id, filename) => {
    const token = localStorage.getItem("vape_token");
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/safety-sheets/${id}/download`, {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok) throw new Error("Impossible de télécharger");
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename || "fiche.pdf"; a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        toast.error(e.message);
      }
    })();
  };

  const search = (e) => { e.preventDefault(); load(q.trim()); };

  return (
    <DashboardLayout title="Fiches de sécurité">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Upload zone */}
        <div className="lg:col-span-4 border border-zinc-800 p-6 space-y-4 h-fit">
          <div>
            <div className="font-display text-lg font-bold tracking-tight">Ajouter une fiche</div>
            <div className="text-xs text-zinc-500 mt-1">PDF ou image · max {MAX_MB} Mo</div>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Marque (optionnel)</label>
            <Input data-testid="safety-brand" value={brand} onChange={(e) => setBrand(e.target.value)}
              placeholder="Ex. Alfaliquid" className="rounded-none bg-zinc-900 border-zinc-800" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Libellé (optionnel)</label>
            <Input data-testid="safety-label" value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex. FDS Base nicotine 20mg" className="rounded-none bg-zinc-900 border-zinc-800" />
          </div>

          <label
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            className={`block border border-dashed p-8 cursor-pointer transition-colors text-center ${dragOver ? "border-red-500 bg-red-500/5" : "border-zinc-700 hover:border-red-500/60"}`}
            data-testid="safety-drop"
          >
            <Upload className="w-8 h-8 mx-auto text-zinc-500" />
            <div className="text-sm text-zinc-300 mt-3">
              {uploading ? "Envoi en cours…" : "Glissez un fichier ici ou cliquez"}
            </div>
            <div className="text-[10px] font-mono text-zinc-500 mt-2 uppercase tracking-widest">PDF · JPG · PNG</div>
            <input
              ref={fileRef}
              data-testid="safety-input"
              type="file"
              multiple
              accept=".pdf,image/*,application/pdf"
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
            />
          </label>
        </div>

        {/* List */}
        <div className="lg:col-span-8 border border-zinc-800 p-6">
          <form onSubmit={search} className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="safety-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher par marque, libellé ou nom de fichier…"
                className="rounded-none bg-zinc-900 border-zinc-800 pl-10"
              />
            </div>
            <Button type="submit" className="bg-red-500 hover:bg-red-600 rounded-none">Rechercher</Button>
            {q && (
              <Button type="button" variant="outline" onClick={() => { setQ(""); load(""); }}
                className="rounded-none border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:text-white">
                Effacer
              </Button>
            )}
          </form>

          <div className="text-xs font-mono text-zinc-500 mb-3">
            {items.length} fiche{items.length > 1 ? "s" : ""}
          </div>

          {items.length === 0 ? (
            <div className="border border-dashed border-zinc-800 py-16 text-center text-zinc-500 text-sm">
              Aucune fiche de sécurité. Déposez-en une pour commencer.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {items.map((s) => (
                <div key={s.id} data-testid={`safety-card-${s.id}`}
                  className="border border-zinc-800 bg-zinc-950 p-4 flex gap-3">
                  <div className="w-10 h-10 bg-red-500/10 border border-red-500/30 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white truncate">{s.label || s.filename}</div>
                    <div className="text-xs text-zinc-500 truncate">
                      {s.brand && <span className="text-zinc-300 mr-2">{s.brand}</span>}
                      <span className="font-mono">{s.filename}</span>
                    </div>
                    <div className="text-[10px] font-mono text-zinc-500 mt-1 uppercase tracking-widest">
                      {fmtSize(s.size_bytes)} · {fmtDate(s.created_at)}
                    </div>
                    <div className="flex items-center gap-1 mt-2">
                      <button data-testid={`safety-open-${s.id}`} onClick={() => openInline(s.id)}
                        className="text-xs text-zinc-400 hover:text-white px-2 py-1 border border-zinc-800 hover:border-zinc-700 inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Ouvrir
                      </button>
                      <button data-testid={`safety-dl-${s.id}`} onClick={() => download(s.id, s.filename)}
                        className="text-xs text-zinc-400 hover:text-white px-2 py-1 border border-zinc-800 hover:border-zinc-700 inline-flex items-center gap-1">
                        <Download className="w-3 h-3" /> Télécharger
                      </button>
                      <button data-testid={`safety-del-${s.id}`} onClick={() => del(s.id, s.filename)}
                        className="ml-auto text-zinc-500 hover:text-red-500 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function fmtSize(b) {
  const n = Number(b) || 0;
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(2)} Mo`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" });
}
